import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trash, Upload, Camera, Sparkles, CheckCircle2, Phone as PhoneIcon, DollarSign, CreditCard } from "lucide-react";
import ZelleInstructions from "./ZelleInstructions";
import SmsConsent from "./SmsConsent";
import PromoField, { promoDiscount } from "./PromoField";
import PromoStrip from "./PromoStrip";
import TransportCalculator from "./TransportCalculator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE_PHOTOS = {
  junk: {
    label: "Garage Junk Overload",
    url: "https://images.unsplash.com/photo-1526726538690-5cbf956ae2fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  drywall: {
    label: "4-inch Drywall Hole",
    url: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  furniture: {
    label: "IKEA Pax Flatpack",
    url: "https://images.unsplash.com/photo-1595428774223-ef52624120d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  handyman: {
    label: "Basement Fixture Job",
    url: "https://images.unsplash.com/photo-1581783898377-1c85bf937427?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  transport: {
    label: "Couch Across Town",
    url: "https://images.unsplash.com/photo-1600518464441-9154a4dea21b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
};

const CATEGORIES = [
  { id: "junk", label: "Junk Removal" },
  { id: "drywall", label: "Drywall / Hole Fix" },
  { id: "furniture", label: "Furniture Assembly" },
  { id: "handyman", label: "General Handyman" },
  { id: "transport", label: "Delivery / Transport" },
];

async function urlToBase64(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => {
      const [, b64] = String(r.result).split(",");
      resolve({ b64, mime: blob.type || "image/jpeg" });
    };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => {
      const [, b64] = String(r.result).split(",");
      resolve({ b64, mime: file.type || "image/jpeg", preview: r.result });
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function PhotoQuoteEngine() {
  const [service, setService] = useState("junk");
  const [preview, setPreview] = useState(null);
  const [imgB64, setImgB64] = useState(null);
  const [imgMime, setImgMime] = useState("image/jpeg");
  const [notes, setNotes] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [quote, setQuote] = useState(null);
  const [booking, setBooking] = useState({ name: "", phone: "", email: "", address: "", zip_code: "", preferred_date: "", preferred_window: "8am - 12pm", sms_consent: false });
  const { user: customer } = useCustomerAuth();
  useEffect(() => {
    if (customer) setBooking((b) => ({ ...b, name: b.name || customer.name || "", email: b.email || customer.email || "" }));
  }, [customer]);
  const [paymentMethod, setPaymentMethod] = useState("zelle");
  const [zelleBooking, setZelleBooking] = useState(null);
  const [bookedRef, setBookedRef] = useState(null);
  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [promo, setPromo] = useState(null);
  const discLow = quote ? promoDiscount(promo, Number(quote.price_low)) : 0;
  const discHigh = quote ? promoDiscount(promo, Number(quote.price_high)) : 0;
  const fileInputRef = useRef(null);
  const [rebook, setRebook] = useState(null);

  useEffect(() => {
    const applyRebook = (saved) => {
      setRebook(saved);
      if (saved.service_type) {
        const id = String(saved.service_type).toLowerCase();
        const match = CATEGORIES.find((c) => c.id === id) || CATEGORIES.find((c) => id.includes(c.id));
        if (match) setService(match.id);
      }
      setBooking((b) => ({ ...b, name: saved.name || b.name, phone: saved.phone || b.phone, email: saved.email || b.email, address: saved.address || b.address, zip_code: saved.zip_code || b.zip_code, preferred_window: saved.preferred_window || b.preferred_window }));
      setTimeout(() => document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" }), 150);
    };
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem("hp_rebook") || "null"); } catch {}
    if (saved) { sessionStorage.removeItem("hp_rebook"); applyRebook(saved); return; }
    const ref = new URLSearchParams(window.location.search).get("rebook");
    if (ref) {
      axios.get(`${API}/rebook/${ref}`).then(({ data }) => applyRebook(data)).catch(() => toast.error("We couldn't find that past job — you can still book fresh below."));
      window.history.replaceState({}, "", `${window.location.pathname}#quote`);
    }
  }, []);

  const quoteWithoutPhoto = async () => {
    setAnalyzing(true);
    setQuote(null);
    try {
      const { data } = await axios.post(`${API}/quote/analyze`, { service_type: service, notes: `Repeat booking of ${rebook?.ref_id || "a previous job"}. ${notes}`.trim() });
      setQuote(data);
      toast.success("Baseline quote ready — fill in your date and you're booked");
      setTimeout(() => document.querySelector("#quote-result")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    } catch { toast.error("Could not create your quote. Try again."); } finally { setAnalyzing(false); }
  };

  const resetImage = () => { setPreview(null); setImgB64(null); setQuote(null); setBookedRef(null); };

  const onFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WEBP image.");
      return;
    }
    const { b64, mime, preview } = await fileToBase64(file);
    setImgB64(b64); setImgMime(mime); setPreview(preview); setQuote(null); setBookedRef(null);
  };

  const onSample = async (id) => {
    const s = SAMPLE_PHOTOS[id];
    try {
      const { b64, mime } = await urlToBase64(s.url);
      setImgB64(b64); setImgMime(mime); setPreview(s.url); setQuote(null); setBookedRef(null);
      setService(id);
      toast.success(`Loaded sample: ${s.label}`);
    } catch {
      toast.error("Could not load sample image.");
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    setQuote(null);
    try {
      const { data } = await axios.post(`${API}/quote/analyze`, {
        service_type: service,
        image_base64: imgB64,
        image_mime: imgMime,
        notes,
      });
      setQuote(data);
      toast.success(`Quote ready — ref ${data.ref_id}`);
      setTimeout(() => document.querySelector("#quote-result")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    } catch (e) {
      toast.error("Could not analyze photo. Try again.");
    } finally { setAnalyzing(false); }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    setSubmittingBooking(true);
    try {
      const payload = {
        quote_id: quote?.id,
        service_type: service,
        ...booking,
        price_low: quote?.price_low || 0,
        price_high: quote?.price_high || 0,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
        promo_code: promo?.code || null,
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      if (paymentMethod === "card" && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      // Zelle
      setZelleBooking(data);
      setBookedRef(data.ref_id);
      toast.success(`Booked — send $${data.deposit_amount} deposit to lock ${data.ref_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save booking. Try again.");
    } finally { setSubmittingBooking(false); }
  };

  return (
    <section id="quote" data-testid="photo-quote-section" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="font-tactical text-[11px] uppercase tracking-[0.25em] text-[#0C2E2B] mb-2">
              AI Photo Estimator
            </div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-zinc-900 leading-tight max-w-3xl">
              Real prices. From one photo. In seconds.
            </h2>
          </div>
          <p className="text-sm text-zinc-500 max-w-sm font-mono-data">
            Powered by Gemini vision · Trained on 12,400+ US home jobs
          </p>
        </div>

        <PromoStrip scope="services" compact />

        {rebook && (
          <div data-testid="rebook-banner" className="mb-6 rounded-lg border-2 border-[#FFDF00] bg-[#FFDF00]/15 p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-tactical text-[10px] uppercase tracking-[0.25em] text-[#0C2E2B]/70">Repeat booking</div>
              <div className="font-display font-black text-lg text-[#0C2E2B]">Same as {rebook.ref_id} · {String(rebook.service_type || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())} at {rebook.address}</div>
              <p className="text-xs text-zinc-600 mt-0.5">Your details are filled in below. Upload a fresh photo for an exact quote, or skip it and book at the baseline rate.</p>
            </div>
            <div className="flex gap-2">
              <Button data-testid="rebook-skip-photo-btn" onClick={quoteWithoutPhoto} disabled={analyzing || !!quote} className="rounded-md bg-[#0C2E2B] text-[#FFDF00] hover:bg-[#0C2E2B]/90 font-black h-10">{analyzing ? "One sec…" : "Book without a photo"}</Button>
              <Button data-testid="rebook-dismiss-btn" variant="outline" onClick={() => setRebook(null)} className="rounded-md h-10 font-bold">Dismiss</Button>
            </div>
          </div>
        )}

        <Tabs value={service} onValueChange={setService}>
          <TabsList className="w-full grid grid-cols-2 md:grid-cols-5 h-auto bg-zinc-100 p-1 rounded-lg">
            {CATEGORIES.map((c) => (
              <TabsTrigger
                key={c.id}
                value={c.id}
                data-testid={`service-category-tab-${c.id}`}
                className="data-[state=active]:bg-[#0C2E2B] data-[state=active]:text-white font-bold text-xs sm:text-sm py-3 rounded-md"
              >
                {c.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATEGORIES.map((c) => (
            <TabsContent key={c.id} value={c.id} className="mt-8">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <Card className="lg:col-span-3 p-6 border-2 border-zinc-200">
                  {c.id === "transport" && (
                    <TransportCalculator
                      notes={notes}
                      disabled={!!bookedRef}
                      onBook={(q) => { setQuote(q); setBookedRef(null); setTimeout(() => document.querySelector("#quote-result")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100); }}
                    />
                  )}
                  <div
                    className="relative rounded-lg border-2 border-dashed border-zinc-300 bg-zinc-50 min-h-[320px] flex items-center justify-center overflow-hidden"
                    data-testid="photo-dropzone"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
                  >
                    {preview ? (
                      <>
                        <img src={preview} alt="preview" className="w-full h-[320px] object-cover" />
                        {analyzing && (
                          <div className="absolute inset-0 pointer-events-none">
                            <div className="scan-grid absolute inset-0 opacity-60" />
                            <div className="scan-line" />
                            <div className="absolute top-3 left-3 bg-black/70 text-white text-[11px] font-mono-data px-2 py-1 rounded">
                              SCANNING · {c.label}
                            </div>
                          </div>
                        )}
                        <button
                          data-testid="photo-clear-btn"
                          onClick={resetImage}
                          className="absolute top-3 right-3 bg-white/90 backdrop-blur border border-zinc-200 rounded-md px-2 py-1 text-xs font-bold flex items-center gap-1 hover:bg-white"
                        >
                          <Trash className="w-3 h-3" /> Clear
                        </button>
                      </>
                    ) : (
                      <div className="text-center p-8">
                        <Upload className="w-10 h-10 mx-auto text-zinc-400 mb-3" />
                        <p className="font-bold text-zinc-800">Drag & drop a photo, or</p>
                        <div className="flex gap-2 justify-center mt-3">
                          <Button
                            data-testid="photo-upload-file-btn"
                            onClick={() => fileInputRef.current?.click()}
                            variant="outline"
                            className="rounded-md font-bold"
                          >
                            <Upload className="w-4 h-4 mr-2" /> Choose File
                          </Button>
                          <Button
                            data-testid="photo-upload-camera-btn"
                            onClick={() => fileInputRef.current?.click()}
                            variant="outline"
                            className="rounded-md font-bold"
                          >
                            <Camera className="w-4 h-4 mr-2" /> Use Camera
                          </Button>
                        </div>
                        <input
                          ref={fileInputRef}
                          data-testid="photo-file-input"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => onFile(e.target.files?.[0])}
                        />
                        <p className="text-xs text-zinc-500 mt-4">Or try a sample photo below · JPEG / PNG / WEBP</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
                    {Object.entries(SAMPLE_PHOTOS).map(([id, s]) => (
                      <button
                        key={id}
                        data-testid={`sample-photo-${id}-btn`}
                        onClick={() => onSample(id)}
                        className="group relative rounded-md overflow-hidden border-2 border-zinc-200 hover:border-[#FFDF00] transition-colors"
                      >
                        <img src={s.url} alt={s.label} className="w-full h-16 object-cover" />
                        <div className="absolute inset-0 bg-black/40 flex items-end p-1">
                          <span className="text-[10px] font-bold text-white leading-tight">{s.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="quote-notes" className="text-xs font-bold uppercase tracking-wider text-zinc-700">
                      {service === "transport" ? "Pickup & drop-off details (both addresses, stairs, weight)" : "Optional notes (stairs, access, urgency)"}
                    </Label>
                    <Textarea
                      id="quote-notes"
                      data-testid="quote-notes-input"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={service === "transport" ? "e.g. Pick up in Richfield 55423, deliver to Maple Grove; 2nd-floor walkup at drop-off; sectional couch ~150 lb" : "e.g. 2nd floor walkup, need it gone before Saturday"}
                      className="mt-1 rounded-md"
                      rows={2}
                    />
                    {service === "transport" && (
                      <p className="text-[11px] text-zinc-500 mt-1.5" data-testid="transport-pricing-note">
                        Flat <b>$89.99</b> within 25 miles of 55423 · +$0.75/mile beyond · +$10 stairs/obstacles · +$100 extra-heavy items (up to 200 lb) · nothing over 300 lb.{" "}
                        <Link to="/terms#delivery" target="_blank" className="underline font-semibold text-[#0C2E2B]">Delivery &amp; Transport Terms</Link>
                      </p>
                    )}
                  </div>

                  <Button
                    data-testid="quote-calculate-btn"
                    onClick={analyze}
                    disabled={!preview || analyzing}
                    className="btn-kinetic rounded-md w-full mt-4 py-6 text-sm"
                  >
                    {analyzing ? (
                      <>
                        <Sparkles className="w-4 h-4 mr-2 animate-pulse" /> Analyzing photo...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" /> Calculate My Instant Quote
                      </>
                    )}
                  </Button>
                </Card>

                <Card id="quote-result" className="lg:col-span-2 p-6 border-2 border-[#0C2E2B] bg-zinc-50 relative overflow-hidden">
                  <div className="absolute top-0 right-0 stripe-dark w-20 h-20 opacity-90" />
                  <div className="font-tactical text-[11px] uppercase tracking-[0.25em] text-[#0C2E2B]">Instant Estimate</div>
                  {!quote ? (
                    <div className="mt-4 space-y-3 text-zinc-600 text-sm">
                      <p>Your itemized quote will appear here after we scan the photo.</p>
                      <ul className="space-y-2 mt-4 text-xs">
                        {["Item + volume detection", "Labor & disposal breakdown", "Truck / materials estimate", "Same-day booking window"].map((x) => (
                          <li key={x} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-[#FFDF00] rounded-full" /> {x}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : bookedRef && zelleBooking ? (
                    <div className="mt-4" data-testid="booking-success">
                      <ZelleInstructions
                        zelle={zelleBooking.zelle}
                        refId={zelleBooking.ref_id}
                        purpose="booking"
                        cancellationPolicy={zelleBooking.cancellation_policy}
                      />
                      <div className="mt-3 text-[11px] text-zinc-500 text-center">
                        Crew will call 30 min before arrival on {zelleBooking.preferred_date}.
                      </div>
                    </div>
                  ) : bookedRef ? (
                    <div className="mt-4 space-y-4" data-testid="booking-success">
                      <CheckCircle2 className="w-12 h-12 text-emerald-600" />
                      <h3 className="font-display font-black text-2xl text-zinc-900">You're on the schedule.</h3>
                      <div className="bg-white rounded-md p-4 border border-zinc-200">
                        <div className="text-xs font-tactical uppercase tracking-widest text-zinc-500">Confirmation</div>
                        <div className="font-mono-data font-bold text-xl text-[#0C2E2B]">{bookedRef}</div>
                        <div className="text-xs text-zinc-500 mt-2">Text & email confirmation dispatched. Crew will call 30 min before arrival.</div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4">
                      <div className="flex items-baseline gap-1">
                        {quote.price_low === quote.price_high ? (
                          <span className="font-display font-black text-4xl text-[#0C2E2B]" data-testid="quote-price-range-display">${Number(quote.price_low).toFixed(2)}</span>
                        ) : (
                          <>
                            <span className="font-display font-black text-4xl text-zinc-900" data-testid="quote-price-range-display">
                              ${Math.round(quote.price_low)}
                            </span>
                            <span className="font-display font-black text-2xl text-zinc-400">—</span>
                            <span className="font-display font-black text-4xl text-[#0C2E2B]">
                              ${Math.round(quote.price_high)}
                            </span>
                          </>
                        )}
                      </div>
                      {promo && (discLow > 0 || discHigh > 0) && (
                        <div data-testid="quote-promo-price" className="mt-1 flex flex-wrap items-baseline gap-2">
                          <span className="font-tactical text-[10px] uppercase tracking-widest text-emerald-700">with {promo.code}</span>
                          <span className="font-display font-black text-2xl text-emerald-700">
                            {quote.price_low === quote.price_high
                              ? `$${(Number(quote.price_low) - discLow).toFixed(2)}`
                              : `$${Math.round(Number(quote.price_low) - discLow)} — $${Math.round(Number(quote.price_high) - discHigh)}`}
                          </span>
                          <span className="text-[11px] text-zinc-500">{promo.label} — taken off the final job price · $25 deposit unchanged</span>
                        </div>
                      )}
                      <div className="mt-1 text-xs text-zinc-500 font-mono-data">
                        REF {quote.ref_id} · {quote.time_estimate} · confidence: {quote.confidence}
                      </div>
                      <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{quote.description}</p>

                      {quote.items_detected?.length > 0 && (
                        <div className="mt-4">
                          <div className="text-[10px] font-tactical uppercase tracking-widest text-zinc-500 mb-2">Detected</div>
                          <div className="flex flex-wrap gap-1.5">
                            {quote.items_detected.slice(0, 6).map((it, i) => (
                              <span key={`item-${it}-${i}`} className="text-[11px] font-bold bg-[#0C2E2B] text-white px-2 py-1 rounded-sm">{it}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {quote.breakdown?.length > 0 && (
                        <div className="mt-4 border-t border-zinc-200 pt-3 space-y-1.5">
                          {quote.breakdown.map((b, i) => (
                            <div key={`bd-${b.label}-${i}`} className="flex justify-between text-sm">
                              <span className="text-zinc-600">{b.label}</span>
                              <span className="font-mono-data font-bold text-zinc-900">${Math.round(b.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <form onSubmit={submitBooking} className="mt-5 space-y-2" data-testid="booking-form">
                        <div className="grid grid-cols-2 gap-2">
                          <Input data-testid="booking-form-name-input" placeholder="Full name" required value={booking.name} onChange={(e) => setBooking({ ...booking, name: e.target.value })} className="rounded-md" />
                          <Input data-testid="booking-form-phone-input" placeholder="Phone" required value={booking.phone} onChange={(e) => setBooking({ ...booking, phone: e.target.value })} className="rounded-md" />
                        </div>
                        <Input data-testid="booking-form-email-input" type="email" placeholder="Email (optional)" value={booking.email} onChange={(e) => setBooking({ ...booking, email: e.target.value })} className="rounded-md" />
                        <Input data-testid="booking-form-address-input" placeholder="Service address" required value={booking.address} onChange={(e) => setBooking({ ...booking, address: e.target.value })} className="rounded-md" />
                        <div className="grid grid-cols-2 gap-2">
                          <Input data-testid="booking-form-zip-input" placeholder="Zip" required value={booking.zip_code} onChange={(e) => setBooking({ ...booking, zip_code: e.target.value })} className="rounded-md" />
                          <Input data-testid="booking-form-date-input" type="date" required value={booking.preferred_date} onChange={(e) => setBooking({ ...booking, preferred_date: e.target.value })} className="rounded-md" />
                        </div>
                        <select
                          data-testid="booking-form-window-select"
                          value={booking.preferred_window}
                          onChange={(e) => setBooking({ ...booking, preferred_window: e.target.value })}
                          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm bg-white"
                        >
                          <option>8am - 12pm</option>
                          <option>12pm - 4pm</option>
                          <option>4pm - 8pm</option>
                        </select>
                        <SmsConsent checked={booking.sms_consent} onChange={(v) => setBooking({ ...booking, sms_consent: v })} context="booking" testId="booking-sms-consent" />
                        <PromoField scope="services" testPrefix="booking-promo" subtotal={Number(quote.price_high || quote.price_low || 0)} promo={promo} email={booking.email}
                          onApply={(p) => { setPromo(p); toast.success(p.message); }} onClear={() => setPromo(null)} />

                        <div>
                          <div className="text-[10px] font-tactical uppercase tracking-widest text-zinc-600 mb-1.5">$25 deposit · pay by</div>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              data-testid="booking-method-zelle-btn"
                              onClick={() => setPaymentMethod("zelle")}
                              className={`p-2 rounded-md border-2 text-left transition-colors ${paymentMethod === "zelle" ? "border-[#FFDF00] bg-[#FFDF00]/20" : "border-zinc-200 bg-white"}`}
                            >
                              <DollarSign className="w-3.5 h-3.5 text-[#0C2E2B] inline" />
                              <span className="font-bold text-xs text-[#0C2E2B] ml-1">Zelle</span>
                            </button>
                            <button
                              type="button"
                              data-testid="booking-method-card-btn"
                              onClick={() => setPaymentMethod("card")}
                              className={`p-2 rounded-md border-2 text-left transition-colors ${paymentMethod === "card" ? "border-[#FFDF00] bg-[#FFDF00]/20" : "border-zinc-200 bg-white"}`}
                            >
                              <CreditCard className="w-3.5 h-3.5 text-[#0C2E2B] inline" />
                              <span className="font-bold text-xs text-[#0C2E2B] ml-1">Card</span>
                            </button>
                          </div>
                          <p className="text-[10px] text-red-700 mt-1.5">
                            Cancellations within 24 hours forfeit the deposit.
                          </p>
                        </div>

                        <Button data-testid="booking-form-submit-btn" type="submit" disabled={submittingBooking} className="btn-kinetic rounded-md w-full py-5 text-xs">
                          {submittingBooking ? "Booking..." : paymentMethod === "card" ? "Continue to card · $25 deposit" : "Book Now · Pay $25 by Zelle"}
                        </Button>
                        <p className="text-[10px] text-zinc-500 text-center">
                          Or call <a href="tel:+16129907871" className="font-bold text-[#0C2E2B]"><PhoneIcon className="w-3 h-3 inline" /> (612) 990-7871</a> to speak with dispatch
                        </p>
                      </form>
                    </div>
                  )}
                </Card>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
